import {FormSectionElements, FormSections, FormSubSections, ProgramContainer} from "../entities/formTemplateDataType";
import validator from 'validator'
import Utils from "./Utils";
import { SCRIPT_ENV } from "../config/RuntimeConfig";

import { getRootComponent } from "./ElementUtils";
import { Address } from "src/runtimeComponent/subComponent/AddressSearchComponent";

export default class FormValidationUtils {
    private static normalizeEscapedParentheses(pattern: string): string {
        // Handle over-escaped patterns like "\\\\(" that should be "\\(".
        return pattern.replace(/\\\\([()])/g, '\\$1');
    }

    private static createSafeRegex(pattern: string, option?: string): RegExp | null {
        try {
            return option ? new RegExp(pattern, option) : new RegExp(pattern);
        } catch (firstError) {
            const normalizedPattern = FormValidationUtils.normalizeEscapedParentheses(pattern);
            if (normalizedPattern !== pattern) {
                try {
                    return new RegExp(normalizedPattern);
                } catch {
                    // fall through to error log below
                }
            }

            console.error('Invalid regex pattern:', pattern, firstError);
            return null;
        }
    }

    static markFieldDirty(field: FormSectionElements) {
        field.IsDirty = true;
    }
    static
        // The month value should be 0-based, so subtract 1 from it
        evalPhonedRegularExpression(field: FormSectionElements) {
        let val = field.Value?.replace(/\s+|-+/g, ''); // Remove spaces and hyphens
        if (!val) return;

        const regex = /^\d{10}$/; // Ensures exactly 10 digits
        if (regex.test(val)) {
            field.Valid = true;
            const match = val.match(/^(\d{3})(\d{3})(\d{4})$/);
            field.Value = `(${match![1]}) ${match![2]}-${match![3]}`;
        } else {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = val.length === 0;
        }
    }


    static controlValid(control: FormSectionElements) {
        if (control.Optional == 'Y') {
            return true;
        }

        if (control.Value == "" || control.Value == null) {
            return false;
        }

        return true;
    }

    static isValidDate(dateString) {
        if (!dateString) {
            return false;
        }
        // Split the date string into month, day, and year
        const [month, day, year] = dateString.split('/');

        // Create a new Date object using the provided values
        // The month value should be 0-based, so subtract 1 from it
        const date = new Date(year, month - 1, day);

        // Check if the parsed date is valid
        // Valid date should have the same values for year, month, and day as the input
        // Additionally, make sure the year is within a reasonable range
        return (
            date.getFullYear() == year &&
            date.getMonth() + 1 == month &&
            date.getDate() == day &&
            year >= 1500 &&
            year <= 2099
        );
    }

    static isDateTime(value) {
        const dateObject = new Date(value);
        return !isNaN(dateObject.getTime());
    }

    static isTime(value: string | null | undefined): boolean {
        if (typeof value !== 'string' || value.trim() === '') {
            return false;
        }
        const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

        return timeRegex.test(value.trim());
    }

    static runJavascript(field: FormSectionElements) {
        if (field.Script && Utils.isNotNullOrEmpty(field.Script)) {
            try {
                const customJSFunc = eval(`(${field.Script})`);
                if (customJSFunc && typeof customJSFunc === 'function') {
                    customJSFunc(field, SCRIPT_ENV);
                }
            } catch (ex) {
                console.log(ex);
            }

        }
    }

    static runEventJavascript(field: FormSectionElements) {
        try {
            FormValidationUtils.runJavascript(field);
            if (field.EventScript && Utils.isNotNullOrEmpty(field.EventScript)) {
                const customEventFunc = eval(`(${field.EventScript})`);
                if (typeof customEventFunc === 'function') {
                    customEventFunc(field, SCRIPT_ENV);
                }
            }
        } catch (ex) {
            console.log('Error executing event script:', ex);
        }
    }

    static runInitJavascript(field: FormSectionElements) {
        if (field.InitScript && Utils.isNotNullOrEmpty(field.InitScript)) {
            try {
                const customJSFunc = eval(`(${field.InitScript})`);
                if (customJSFunc && typeof customJSFunc === 'function') {
                    customJSFunc(field, SCRIPT_ENV);
                }
            } catch (ex) {
                console.log(ex);
            }

        }
    }
    static runKeyStrokeJavascript(field: FormSectionElements) {
        if (field.KeyStrokeScript && Utils.isNotNullOrEmpty(field.KeyStrokeScript)) {
            try {
                const customJSFunc = eval(`(${field.KeyStrokeScript})`);
                if (customJSFunc && typeof customJSFunc === 'function') {
                    customJSFunc(field, SCRIPT_ENV);
                }
            } catch (ex) {
                console.log(ex);
            }

        }
    }

    static invalidControls(formSection: FormSections, subSectionToDuplicate?: string) {

        let invalidList: FormSectionElements[] = [];
        let fieldList: FormSectionElements[] = [];

        formSection.ListOfFormSubSections.forEach((sub) => {
            if (subSectionToDuplicate && subSectionToDuplicate != sub.ENUTitle) {
                return;
            }
            if (sub.Visible===false) return;
            sub.ListOfFormSectionElements.filter(p=>p.Visible).forEach(field => {
                if ((field.Visible) && field.Datatype != 'HiddenField' && field.Datatype != 'Label' && field.Datatype != 'HtmlContent'
                    && field.Datatype != 'Button'
                ) {
                    if (field.Datatype != 'Row') {
                        fieldList.push(field)
                    } else {
                        field.subSectionList[0].ListOfFormSectionElements.forEach(field=>{
                            fieldList.push(field);
                        })
                    }

                }
            })
        })

        if (fieldList.length == 0) {
            return invalidList;
        }

        fieldList.filter(p=>p.Visible==true).forEach((field: FormSectionElements) => {
            FormValidationUtils.validate(field);
            if (!field.Valid && field.Disabled != true) {
                field.Touched = true;
                invalidList.push(field);
            } else {
                FormValidationUtils.runJavascript(field);
                if (!field.Valid && field.Disabled != true) {
                    field.Touched = true;
                    if (invalidList.filter(p => p.Id == field.Id).length == 0) {
                        invalidList.push(field);
                    }
                }
            }
        })

        // Trigger file error dialog for inline-error mode file uploads with errors
        console.log('Checking for file upload errors in invalidList:', invalidList.length);
        invalidList.forEach((field: FormSectionElements) => {
            console.log('Checking field:', {
                datatype: field.Datatype,
                uploadStyle: field.FileUpload?.UploadStyle,
                hasValue: !!field.Value,
                isArray: Array.isArray(field.Value),
                fileCount: Array.isArray(field.Value) ? field.Value.length : 0,
                hasErrors: Array.isArray(field.Value) ? field.Value.some((file: any) => file.hasError) : false
            });

            if (field.Datatype === 'File' &&
                field.FileUpload?.UploadStyle === 'inline-error' &&
                field.Value &&
                Array.isArray(field.Value) &&
                field.Value.some((file: any) => file.hasError)) {
                console.log('Setting ShowFileErrorDialog to true for field:', field.Id);
                field.ShowFileErrorDialog = true;
            }
        });

        console.log("invalidList", invalidList);
        return invalidList;
    }

    static validate(field: FormSectionElements) {
        switch (field.Datatype) {
            case "Address":
                // console.log(`address: ${field.Valid}; ${field.Value}`);
                FormValidationUtils.validateAddress(field);
                break;
            case "Currency":
                FormValidationUtils.validateCurrency(field);
                break;
            case "Text":
            case "MultiText":
            case "DateTime":
                FormValidationUtils.validateText(field);
                FormValidationUtils.evalRegularExpression(field);
                break;
            case "Time":
                FormValidationUtils.validateText(field);
                FormValidationUtils.evalRegularExpression(field);
                break;
            case "SelectWithRichText":
                FormValidationUtils.validateSelectWithRichText(field);
                break;
            case "SearchAutocomplete":
                FormValidationUtils.validateAutoSearchInput(field);
                break;    
            case "RadioButtonWithRichText":
                FormValidationUtils.validateRadioButton(field);
                break;
            case "CheckGroup":
                FormValidationUtils.validateCheckGroup(field);
                break;
            case "Checkbox":
                FormValidationUtils.validateCheckbox(field);
                break;
            case "Dropdown":
                FormValidationUtils.validateDropdown(field);
                break;
            case "RadioButton":
                FormValidationUtils.validateRadioButton(field);
                break;
            case "HealthCard":
                this.processHealthCardValidation(field);
                break;
            case "LicensePlate":
                FormValidationUtils.validateLicensePlate(field);
                break;
            case "CheckPlateAndRenderImage":
                if (!field.Valid) {  // To prevent reset of the Valid flag.
                    return;
                }
                // check for plate type and want graphic
                let plateType = 'PASSENGER';
                let graphicType = '0000';
                let subsection = field.parent as FormSubSections;
                subsection.ListOfFormSectionElements?.forEach(ele => {
                    if (ele.LoadBindName == "plateType" && ele.Value)  {
                        plateType = ele.Value;
                    }
                    if (ele.LoadBindName == "wantGraphic" && ele.Value) {
                        graphicType = ele.Value;
                    }
                });
                FormValidationUtils.validateLicensePlate(field, plateType, graphicType, field.parent.parent.parent.CategoryId);
                break;
            case "BusinessNumber":
                FormValidationUtils.validateBusinessNumber(field);
                break;
            case "DriverLicense":
                FormValidationUtils.validateDriverLicense(field);
                break;
            case "RegistrantIdentificationNumber":
                FormValidationUtils.validateRegistrantIdentificationNumber(field);
                break;
            case "Email":
            case "EmailConfirmation":
                FormValidationUtils.validateEmail(field);
                break;
            case "PhoneNumber":
                FormValidationUtils.validatePhoneNumber(field);
                break;
            case "Date":
                FormValidationUtils.validateDate(field);
                break;
            case "Number":
                FormValidationUtils.validateNumber(field);
                break;
            case "ODSDate":
                FormValidationUtils.validateODSDate(field);
                break;
            case "PostalCode":
                FormValidationUtils.validatePostalCode(field);
                break;
            case "CheckboxWithTextbox":
                FormValidationUtils.validateCheckbobWithInput(field);
                break;
            case "CheckboxWithInlineTextbox":
                FormValidationUtils.validateCheckbobWithInput(field);
                break;
            case "File":
                FormValidationUtils.validateFileUpload(field);
                break;
            case "BXPCheckboxWithInlineTextbox":
                FormValidationUtils.validateBXPCheckboxWithInput(field);
                break;
            case "Row":
                FormValidationUtils.validateRow(field);
                break;
            case "SearchSingleItemFromApi":
                // Invalidated by the API lookup.
                if (field.Api && field.Touched && field.Optional === "N" && !Utils.isNullOrEmpty(field.Value) && !field.Valid) {
                    return;  
                }
                FormValidationUtils.validateMandatory(field);
                FormValidationUtils.evalRegularExpression(field);
                break;
        }
    }

    static processHealthCardValidation(field: FormSectionElements) {
        FormValidationUtils.validateHealthCard(field);
        if (Utils.isNotNullOrEmpty(field?.Value)) {
            FormValidationUtils.evalHeathCardRegularExpression(field)
        }
    }

    static validateMandatory(field: FormSectionElements) {
        const val = field.Value;
        if (field.Optional == "N" && Utils.isNullOrEmpty(field.Value)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            field.Valid = true;
        }
    }

    static validateAddress(field: FormSectionElements) {
        const add = field.Address as Address;

        if (field.allowManualAddress === true && field.Optional === 'Y' && field.Address?.manualAddressChecked !== true) {
            return
        }
     
        const invalidAddressMessageEn = "Address is invalid";
        const invalidAddressMessageFr = "L'adresse est invalide";
        const emptyAddressMessageEn = "You must enter an address";

        // Handle manual address validation
        if (field.Address?.manualAddressChecked === true) {
            // If manual address is checked but Address is missing and Value is empty
            if (!add && !field.Value) {
                field.Valid = false;
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.EmptyErrorMessage = field.EmptyErrorMessage || emptyAddressMessageEn;
                return;
            }
            // If manual address is allowed but not provided and field is required
            if (!add && field.Optional === 'N') {
                field.Valid = false;
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.EmptyErrorMessage = field.EmptyErrorMessage || emptyAddressMessageEn;
                return;
            }
        }

        // If manual address is checked, validate required fields, If manual address is not checked, also need to validate required fields
        if (add) {
            const hasValidUnit =
                !!(add.unitType && add.suiteNumber) ||
                (!add.unitType && !add.suiteNumber);
            //if (add && field.Address?.manualAddressChecked === true) {
            const hasRequiredFields =
                (add.isNoStreetNoAddress ? (!!add.lotType && !!add.lotNumber && !!add.identifier && !!add.identifierNumber && (field.hideTownship || !!add.township)) : true) &&
                (!add.isNoStreetNoAddress && !field.isStreetNumberOptional ? !!add.streetNumber : true) &&
                (!add.isNoStreetNoAddress && !field.isStreetNameOptional && !field.combineStreetFields ? !!add.streetName : true) &&
                (!field.isMunicipalityOptional ? !!add.municipality : true) &&
                (!field.isProvinceOptional ? !!add.province : true) &&
                (!field.isPostCodeOptional ? !!add.postalCode : true) &&
                (field.hideCountry ? true : !!add.country)
                && hasValidUnit;

            field.Valid = hasRequiredFields;
            if (!hasRequiredFields) {
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.EmptyErrorMessage = field.EmptyErrorMessage || emptyAddressMessageEn;
            } else {
                field.ErrorMessage = '';
                field.EmptyErrorMessage = '';
            }
            // Validate postal code if present
            if (add.province !=='OTHER' && add.postalCode && !validator.isPostalCode(add.postalCode, "CA") && field.allowManualAddress === true) {
                field.Valid = false;
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.ErrorMessageFRA = field.ErrorMessageFRA || invalidAddressMessageFr;
            }
            if (add.province === 'OTHER' && field.allowManualAddress === true && !field.isPostCodeOptional && (add.postalCode.length < 1 || add.postalCode.length > 15)) {
                field.Valid = false;
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.ErrorMessageFRA = field.ErrorMessageFRA || invalidAddressMessageFr;
            }
            if (add.province === 'OTHER' && !add.postalCode  && field.allowManualAddress === true && !field.isPostCodeOptional) {
                field.Valid = false;
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.ErrorMessageFRA = field.ErrorMessageFRA || invalidAddressMessageFr;
            }
            if (add.province === 'OTHER' && !add.provinceOther && field.allowManualAddress === true) {
                field.Valid = false;
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.ErrorMessageFRA = field.ErrorMessageFRA || invalidAddressMessageFr;
            }

            return;
        }

        if (!field.Address && !field.Value  && field.allowManualAddress === true && field.Address?.manualAddressChecked !== true) {
            if(field.Optional === 'N'){
                field.Valid = false;
                field.Touched = true;
                field.EmptyErrorMessage = field.EmptyErrorMessage || emptyAddressMessageEn;
                field.ErrorMessage = field.EmptyErrorMessage;
                field.ErrorMessageFRA = field.EmptyErrorMessageFRA || (field.addressType==='Mailing'?  "L'adresse postale doit être renseignée" : field.addressType==="Business" ? "L'adresse de l'entreprise doit être saisie" :"L'adresse résidentielle doit être saisie");
                field.EmptyErrorMessageFRA = field.ErrorMessageFRA;
            }
            return;
        }

        if (!field.Address && field.Value  && field.allowManualAddress === true && field.Address?.manualAddressChecked !== true) {
            const addressRegex = /^(?:((PART\s+)?LOT)|(PART BLOCK)\s\d+\s-\s)?(?:SUITE|APT|UNIT\s\d+\s-\s)?(?:[\d\s\w\.-]+(?:\s-\s[\d\s\w\.-]+)?(?:\s(odd|even))?)(,)?\s[A-Za-zÀ-ÖØ-öø-ÿ\s\d'-]+(?:,\s[A-Za-zÀ-ÖØ-öø-ÿ\s\d'-]+)*,\s(?:AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT),\s[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d(?:,\sCanada)?$/gmi;
            const addressTestRes = addressRegex.test(field.Value);
            if (!addressTestRes) {
                field.Valid = false;
                field.Touched = true;
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.ErrorMessageFRA = field.ErrorMessageFRA || invalidAddressMessageFr;
                console.log('invalid address');
                return;
            }else{
                field.Valid=true;
            }
            return;
          }
        const treatAsOptional = field.Optional === 'Y' || (field.AlwaysDisplayCity && field.AlwaysDisplayProvince);
        if (treatAsOptional) {
            if (!field.Value) {
                field.Valid = true;
                field.Touched = true;
                field.ErrorMessage = '';
                field.ErrorMessageFRA = '';
                return;
            }else{
                //const canadaAddressRegex = /^\s*(?:(?:UNIT|SUITE|APT|APARTMENT)\s+[A-Za-z0-9]+[A-Za-z]?\s*-\s*|#[A-Za-z0-9]+[A-Za-z]?\s*-\s*)?(\d+\s*(?:[A-Za-z]|[1-9]\/[1-9])?)\s+[A-Za-z0-9\s.'/-]+,\s*[A-Za-z\s.'-]+,\s*(AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT),\s*[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d\s*$/i;
                const canadaAddressRegex = /^\s*(?:(?:UNIT|SUITE|APT|APARTMENT)\s+[A-Za-z0-9]+[A-Za-z]?\s*-\s*|#[A-Za-z0-9]+[A-Za-z]?\s*-\s*)?(\d+\s*(?:[A-Za-z]|[1-9]\/[1-9])?)\s+[A-Za-z0-9\s.'/-]+,\s*[A-Za-z\s.'-]+,\s*(AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT),\s*[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d(?:,\s*Canada)?\s*$/i;

                if(canadaAddressRegex.test(field.Value)){
                    return;
                }
            }
            const add = field.Address as Address;
            const hasCityProv = !!add?.municipality && !!add?.province;
            console.log('Address has city and province:', hasCityProv);
            console.log('Address details:', add);
            field.Valid = hasCityProv;
            field.Touched = true;
            if (!hasCityProv) {
                field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                field.ErrorMessageFRA = field.ErrorMessageFRA || invalidAddressMessageFr;
            } else {
                field.ErrorMessage = '';
                field.ErrorMessageFRA = '';
            }
            return;
        }
        if (field.Valid) {
            if (!field.Address && !field.Value && !treatAsOptional) {
                field.Touched = true;
                field.Valid = false;
                field.EmptyErrorMessage = field.EmptyErrorMessage || (field.addressType==='Mailing' ? "Mailing": field.addressType==="Business" ? "Business": "Residential") + " address must be entered";
                field.EmptyErrorMessageFRA = field.EmptyErrorMessageFRA || (field.addressType==='Mailing'?  "L'adresse postale doit être renseignée" : field.addressType==="Business" ? "L'adresse de l'entreprise doit être saisie" :"L'adresse résidentielle doit être saisie");
                field.ErrorMessage = field.EmptyErrorMessage;
                field.ErrorMessageFRA = field.EmptyErrorMessageFRA;
                return;
            }

            if (field.Value && field.Value.length > 0) {
                if (field.allowPartialAddress) {
                    const add = field.Address as Address;
                    if (!add?.municipality || !add?.province) {
                        field.Valid = false;
                        field.Touched = true;
                    }
                    return
                }

                if (field.allowManualAddress) {
                    field.Valid = true;
                    field.Touched = true;
                    return;
                }

                // const addressRegex = /^(?:((PART\s+)?LOT)|(PART BLOCK)\s\d+\s-\s)?(?:SUITE|APT|UNIT\s\d+\s-\s)?(?:[\d\s\w\.-]+(?:\s-\s[\d\s\w\.-]+)?(?:\s(odd|even))?),\s[A-Za-z\s\d]+(?:,\s[A-Za-z\s]+)*,\s(?:ON),\s[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/gmi;
                //const addressRegex = /^(?:((PART\s+)?LOT)|(PART BLOCK)\s\d+\s-\s)?(?:SUITE|APT|UNIT\s\d+\s-\s)?(?:[\d\s\w\.-]+(?:\s-\s[\d\s\w\.-]+)?(?:\s(odd|even))?)(,)?\s[A-Za-zÀ-ÖØ-öø-ÿ\s\d'-]+(?:,\s[A-Za-zÀ-ÖØ-öø-ÿ\s\d'-]+)*,\s(?:AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT),\s[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/gmi;
                const addressRegex = /^(?:((PART\s+)?LOT)|(PART BLOCK)\s\d+\s-\s)?(?:SUITE|APT|UNIT\s\d+\s-\s)?(?:[\d\s\w\.-]+(?:\s-\s[\d\s\w\.-]+)?(?:\s(odd|even))?)(,)?\s[A-Za-zÀ-ÖØ-öø-ÿ\s\d'-]+(?:,\s[A-Za-zÀ-ÖØ-öø-ÿ\s\d'-]+)*,\s(?:AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT),\s[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d(?:,\sCanada)?$/gmi;

                const addressTestRes = addressRegex.test(field.Value);
                if (!addressTestRes) {
                    field.Valid = false;
                    field.Touched = true;
                    field.ErrorMessage = field.ErrorMessage || invalidAddressMessageEn;
                    field.ErrorMessageFRA = field.ErrorMessageFRA || invalidAddressMessageFr;
                    console.log('invalid address');
                    return;
                }else{
                    field.Valid=true;
                }
            }
            FormValidationUtils.validateText(field);
        }
    }

    static validateRow(field: FormSectionElements){
        field.subSectionList[0].ListOfFormSectionElements.forEach(item=>{
            FormValidationUtils.validate(item);
        })
    }

    static validatePostalCode(field: FormSectionElements) {
        const val = field.Value;

        if (val && val.trim() !== "") {
            field.IsEmpty = false;
            if (!validator.isPostalCode(val, "CA")) {
                field.Valid = false;
                field.Touched = true;
            } else {
                field.Valid = true;
            }
        } else {
            field.IsEmpty = true;
            if (field.Optional === "N" || field.Optional === "n") {
                field.Valid = false;
                field.Touched = true;
            } else {
                field.Valid = true;
            }
        }
    }

    static validateNumber(field: FormSectionElements) {
        const val = field.Value;
        if (field.Optional !== 'Y' && !Utils.isNotNullOrEmpty(val)) {
            //    field.ErrorMessage = field.ErrorMessage == null ? field.ENUTitle + " is required" : field.ErrorMessage;
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            if (Utils.isNotNullOrEmpty(val) && !validator.isFloat(String(val))) {
                //       field.ErrorMessage = field.ErrorMessage == null ? field.ENUTitle + " is invald" : field.ErrorMessage;
                field.Valid = false;
                field.Touched = true;
            } else {
                field.Valid = true;
            }
        }
    }

    static calculateDateLimit(field: FormSectionElements, isMin: boolean) {
        const staticDate = isMin ? field.MinDate : field.MaxDate;
        const comparison = isMin ? field.MinDateComparison : field.MaxDateComparison;
        const unit = isMin ? field.MinDateUnit : field.MaxDateUnit;
        const amount = isMin ? parseInt(field.MinDateAmount) : parseInt(field.MaxDateAmount);

        // return static date if well-defined
        if (staticDate) return new Date(staticDate);

        // return null if relative date not well-defined
        if (comparison !== 'today' && (!comparison || !unit || !amount)) return null;

        // otherwise if well-defined AND not static, calculate relative limit
        const today = new Date();
        let limit = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        if (comparison !== 'today') {
            switch (unit) {
                case 'days':
                    limit.setDate(today.getDate() + (comparison === 'before' ? -amount : amount));
                    break;
                case 'weeks':
                    limit.setDate(today.getDate() + (comparison === 'before' ? (-amount) * 7 : amount * 7));
                    break;
                case 'months':
                    limit.setMonth(today.getMonth() + (comparison === 'before' ? -amount : amount));
                    break;
                case 'years':
                    limit.setFullYear(today.getFullYear() + (comparison === 'before' ? -amount : amount));
                    break;
            }
        }
        return limit;
    }

    static validateDate(field: FormSectionElements) {
        const val = field.Value;

        const min = this.calculateDateLimit(field, true);
        const max = this.calculateDateLimit(field, false);

        console.log('min:', min)
        console.log('max:', max)
        console.log('value:', val)

        const isEmpty = Utils.isNullOrEmpty(val);
        const outOfRange = (min && val < min) || (max && val > max);

        if (field.Optional != 'Y' && isEmpty) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
            console.log('empty + req');
        } else if (!isEmpty && outOfRange) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = false;
            console.log('filled + invalid');
        } else {
            field.Valid = true;
            console.log('valid');
        }
    }

    static validateODSDate(field: FormSectionElements) {
        const val = field.Value;
        let effectiveVal = val;
        if (typeof val === 'string' && val.trim() === "") {
            effectiveVal = null;
        } else if (typeof val === 'object' && val !== null && Object.keys(val).length === 0) {
            effectiveVal = null;
        }

        if (effectiveVal && typeof effectiveVal === 'string') {
            field.IsEmpty = false;
            field.Touched = true;
            if (!FormValidationUtils.isValidDate(effectiveVal)) {
                field.Valid = false;
            // if valid date, check if within limits
            } else {
                const min = this.calculateDateLimit(field, true);
                const max = this.calculateDateLimit(field, false);

                 // convert to date object (midnight local)
                const [monthStr, dayStr, yearStr] = effectiveVal.split('/');
                const month = parseInt(monthStr, 10) - 1;
                const day = parseInt(dayStr, 10);
                const year = parseInt(yearStr, 10);
                const dateVal = new Date(year, month, day);

                // check limits
                if ((min && dateVal < min) || (max && dateVal > max)) {
                    field.Valid = false
                } else {
                    field.Valid = true;
                }
                console.log('min:', min);
                console.log('max:', max);
                console.log('val:', dateVal);
            }
        } else {
            field.IsEmpty = true;
            if (field.Optional !== 'Y') {
                field.Valid = false;
                field.Touched = true;
            } else {
                field.Valid = true;
            }
        }
        FormValidationUtils.validateIssueExpiryPair(field);
    }

    static parseODSDateFromField(field: FormSectionElements | null | undefined): Date | null {
        if (!field) {
            return null;
        }
        const year = field.year != null ? parseInt(String(field.year), 10) : NaN;
        const month = field.month != null ? parseInt(String(field.month), 10) : NaN;
        const day = field.day != null ? parseInt(String(field.day), 10) : NaN;
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            const fromYmd = new Date(year, month - 1, day);
            if (!isNaN(fromYmd.getTime())) {
                return fromYmd;
            }
        }
        const value = field.Value;
        if (value == null || String(value).trim() === '') {
            return null;
        }
        const valueText = String(value).trim();
        const fromNative = new Date(valueText);
        if (!isNaN(fromNative.getTime())) {
            return fromNative;
        }
        const mdy = valueText.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
        if (mdy) {
            const m = parseInt(mdy[1], 10) - 1;
            const d = parseInt(mdy[2], 10);
            const y = parseInt(mdy[3], 10);
            const fromMdy = new Date(y, m, d);
            if (!isNaN(fromMdy.getTime())) {
                return fromMdy;
            }
        }
        return null;
    }

    static validateIssueExpiryPair(field: FormSectionElements) {
        const enabledValue = field.DateCompareEnabled;
        const isEnabled = enabledValue === true || String(enabledValue || '').toLowerCase() === 'y' || String(enabledValue || '').toLowerCase() === 'true';
        if (!isEnabled) {
            return;
        }

        const compareWithField = (field.DateCompareWithField || '').trim();
        if (!compareWithField) {
            return;
        }

        const subSection = field.parent as FormSubSections;
        const list = subSection?.ListOfFormSectionElements || [];
        const issueField = list.find(el => el.LoadBindName === compareWithField);
        if (!issueField || issueField.Visible === false || field.Visible === false) {
            return;
        }

        const leftDate = FormValidationUtils.parseODSDateFromField(field);
        const rightDate = FormValidationUtils.parseODSDateFromField(issueField);
        if (!leftDate || !rightDate) {
            return;
        }

        const operator = (field.DateCompareOperator || '>=').trim();
        let isValid = true;
        switch (operator) {
            case '>':
                isValid = leftDate.getTime() > rightDate.getTime();
                break;
            case '>=':
                isValid = leftDate.getTime() >= rightDate.getTime();
                break;
            case '<':
                isValid = leftDate.getTime() < rightDate.getTime();
                break;
            case '<=':
                isValid = leftDate.getTime() <= rightDate.getTime();
                break;
            case '==':
            case '=':
                isValid = leftDate.getTime() === rightDate.getTime();
                break;
            case '!=':
                isValid = leftDate.getTime() !== rightDate.getTime();
                break;
            default:
                isValid = leftDate.getTime() >= rightDate.getTime();
        }

        if (!isValid) {
            const err = field.DateCompareErrorMessage || 'Date comparison validation failed.';
            const errFra = field.DateCompareErrorMessageFRA || err;
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = false;
            field.ErrorMessage = err;
            field.ErrorMessageFRA = errFra;
            field.ErrorMessageRichText = err;
            field.ErrorMessageRichTextFRA = errFra;
        }
    }

    static validateCurrency = (field: FormSectionElements) => {
        const val = field.Value;

        if(Utils.isNullOrEmpty(val)) {
            if (field.Optional == "N") {
                field.Valid = false;
                field.Touched = true;
                field.IsEmpty = true;
            } else {
                field.Valid = true;
            }
        } else {
            if (!validator.isFloat(val)) {
                field.Valid = false;
                field.Touched = true;
            } else {
                const numberVal = Number.parseFloat(val);
                if ((field.MinValue != null && field.MinValue <= numberVal) && (field.MaxValue != null && numberVal <= field.MaxValue)) {
                    field.Valid = true;
                } else {
                    field.Touched = true;
                    field.Valid = false;
                    field.ErrorMessage = field.ErrorMessage || `Please enter a value between ${field.MinValue} and ${field.MaxValue}`;
                }
            }
        }
    }

    static validateText = (field: FormSectionElements) => {
        const val = field.Value;
        if (field.Optional == "N") {
            if (Utils.isNullOrEmpty(val)) {
                field.Valid = false;
                field.Touched = true;
                field.IsEmpty = true;
            } else {
                field.Valid = true;
                field.Touched = true;
                field.IsEmpty = false;
            }
        } else {
            field.Valid = true;
        }
    }

    static evalRegularExpression = (field: FormSectionElements, regexOption?: string) => {
        const val = field.Value;
        if (field.Valid && !Utils.isNullOrEmpty(val)) {
            if (field.RegularExpression) {
                const regexPattern = FormValidationUtils.createSafeRegex(field.RegularExpression, regexOption);
                if (!regexPattern) {
                    field.Valid = false;
                    field.Touched = true;
                    field.IsEmpty = false;
                    return;
                }

                const isMatch = regexPattern.test(val);
                if (!isMatch) {
                    field.Valid = false;
                    field.Touched = true;
                    field.IsEmpty = false;
                }
            }
        }
    }

    static validateSelectWithRichText = (field: FormSectionElements) => {
        const val = field.Value;
        if (field.Optional == "N" && Utils.isNullOrEmpty(field.SelectionCode)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            field.Valid = true;
        }
    }

    static validateCheckGroup = (field: FormSectionElements) => {

        const val = field.Value;
        if (field.Optional == "N" && (val == "" || val == null)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            field.Valid = true;
        }
    }

    static validateRadioButton = (field: FormSectionElements) => {
        const val = field.Value;
        if (field.Optional == "N" && Utils.isNullOrEmpty(val)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            field.Valid = true;
        }
    }

    static validateAutoSearchInput = (field: FormSectionElements) => {
        const val = field.Value;
        if (field.Optional == "N" && Utils.isNullOrEmpty(val)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            field.Valid = true;
        }
    }

    static validateCheckbox = (field: FormSectionElements) => {
        const val = field.Value;
        if ("Y" != field.Optional && (Utils.isNullOrEmpty(val) || val == "N" || val == false)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            field.Valid = true;
        }
    }

    static validateHealthCard = (field: FormSectionElements) => {
        const val = field.Value;
        if ((field.Optional !== "Y" && Utils.isNullOrEmpty(val))) {
            // field.ErrorMessage = "Health Card Number is invalid";
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            field.Valid = this.healthCardOnInputValidation(val, field);
            field.Touched = true;
            field.IsEmpty = false;
        }
    }

    static evalHeathCardRegularExpression = (field: FormSectionElements) => {
        const val = field.Value?.replace(/\s+|-+/g, '');
        const regex = /^(\d{4})(\d{3})(\d{3})([a-z]{2})$/gi;
        if (regex.test(val)) {
            field.Valid = true;
            field.Value = val.replace(regex, '$1-$2-$3-$4').toUpperCase();
        } else {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = false;
        }

        // if (field.Valid) {
        //     const pattern = /^[0-9]{4}(?:[-\s]*)[0-9]{3}(?:[-\s]*)[0-9]{3}(?:[-\s]*)(?:[a-zA-Z]{2})$/;0750023454167326747

        //     if (!pattern.test(val)) {
        //         field.Valid = false;
        //         field.Touched = true;
        //         field.IsEmpty = false;
        //     }
        // }
    }

    static evalDriversLicenseCardRegularExpression = (field: FormSectionElements) => {
        const val = field.Value;
        if (field.Visible && field.Valid) {
            const pattern = /^([A-Za-z]\d{4})[-]?(\d{5})[-]?(\d{5})$/;

            if (!pattern.test(val)) {
                field.Valid = false;
                field.Touched = true;
                field.IsEmpty = false;
            }
        }
    }

    static evalRegistrantIdentificationNumberRegularExpression = (field: FormSectionElements) => {
        const val = field.Value;
        if (field.Visible && field.Valid) {
            const pattern = /^(\d{9})$/;

            if (!pattern.test(val)) {
                field.Valid = false;
                field.Touched = true;
                field.IsEmpty = false;
            }
        }
    }

    static evalLicensePlateRegularExpression = (field: FormSectionElements, plateType = 'PASSENGER', graphicType = '0000', categoryId = null) => {
        const val = field.Value;
        if (field.Valid) {
            const licencePlatePattern  = FormValidationUtils.determinePlatePattern(plateType, graphicType, categoryId);
            // (?!.*([a-z])\1{4}) Negative lookahead to ensure we don't allow 5 repeats of alphanumerics.
            const notMoreThan4SameConsecutiveCharsPattern = /^(?!.*([A-Za-z0-9\s])\1{4})[A-Za-z0-9\s]+(?:[' -][A-Za-z0-9\s]+)*$/;
            const multipleConsecutiveSpacesPattern = /(?<!\.|\s)\s{2,}/;
            const leadingOrTrailingSpacePattern = /^\s+|\s+$/;

            if ((!licencePlatePattern.test(val)) ||
                (!notMoreThan4SameConsecutiveCharsPattern.test(val)) ||
                (multipleConsecutiveSpacesPattern.test(val)) ||
                (leadingOrTrailingSpacePattern.test(val))) {
                field.Valid = false;
                field.Touched = true;
                field.IsEmpty = false;
            }
        }
    }

    static validateLicensePlate = (field: FormSectionElements, plateType = 'PASSENGER', graphicType = '0000', categoryId = null) => {
        // (?!.*([a-z])\1{4}) Negative lookahead to ensure we don't allow 5 repeats of alphanumerics.
        const notMoreThan4SameConsecutiveCharsPattern = /^(?!.*([A-Za-z0-9\s])\1{4})[A-Za-z0-9\s]+(?:[' -][A-Za-z0-9\s]+)*$/;
        const multipleConsecutiveSpacesPattern = /(?<!\.|\s)\s{2,}/;
        const leadingOrTrailingSpacePattern = /^\s+|\s+$/;
        let licencePlatePattern:RegExp;
        if (!field.licensePlateRegex || field.licensePlateRegex===''){
            licencePlatePattern = FormValidationUtils.determinePlatePattern(plateType, graphicType, categoryId);
        }else{
            const customPlatePattern = FormValidationUtils.createSafeRegex(field.licensePlateRegex);
            if (!customPlatePattern) {
                field.Valid = false;
                field.Touched = true;
                field.IsEmpty = false;
                return;
            }

            licencePlatePattern = customPlatePattern;
        }

        if ((field.Optional == "N" && Utils.isNullOrEmpty(field.Value))) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else if (Utils.isNotNullOrEmpty(field.Value) &&
            ((!licencePlatePattern.test(field.Value)) ||
                (!notMoreThan4SameConsecutiveCharsPattern.test(field.Value)) ||
                (multipleConsecutiveSpacesPattern.test(field.Value)) ||
                (leadingOrTrailingSpacePattern.test(field.Value)))) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = false;
        } else {
            field.Valid = true;
        }
    }

    static determinePlatePattern = (plateType: string, graphicType: string, categoryId?: string): RegExp => {
        const generalNonGraphicPattern = /^[A-Za-z0-9\s]{2,8}$/;
        const generalGraphicPattern = /^[A-Za-z0-9\s]{2,6}$/;
        const generalMotorcyclePattern = /^[A-Za-z0-9\s]{0,10}$/;
        const plpGeneralMotorcyclePattern = /^[A-Za-z0-9\s]{2,5}$/;

        if (plateType == 'MOTORCYCLE') {
            if (categoryId?.toLowerCase() ==='plp') {
                return plpGeneralMotorcyclePattern;
            } else {
                return generalMotorcyclePattern;
            }
        }
        if (graphicType === '0000') return generalNonGraphicPattern;

        return generalGraphicPattern;
    }

    static validateBusinessNumber = (field: FormSectionElements) => {
        const pattern = /^[0-9]{9}([A-Z]{2}[0-9]{4})?$/;
        if (field.Optional == "N" && Utils.isNullOrEmpty(field.Value)) {
            // field.ErrorMessage = this.props.langCo=="En"? "Business Number is invalid" :"Le numéro d'entreprise saisi est invalide";
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else if (Utils.isNotNullOrEmpty(field.Value) && !pattern.test(field.Value)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = false;
        } else {
            field.Valid = true;
            Utils.isNullOrEmpty(field.Value) ? field.IsEmpty = true : field.IsEmpty = false;
        }
        // console.log(field)
    }

    static validateDriverLicense = (field: FormSectionElements) => {
        const pattern = /^([A-Za-z]\d{4})[-]?(\d{5})[-]?(\d{5})$/;
        if ((field.Visible && field.Optional == "N" && Utils.isNullOrEmpty(field.Value)) || (!Utils.isNullOrEmpty(field.Value) && !pattern.test(field.Value))) {
            field.Valid = false;
            field.Touched = true;
        } else {
            field.Valid = true;
        }
    }

    static validateRegistrantIdentificationNumber(field: FormSectionElements) {
        const pattern = /^(\d{9})$/;
        if ((field.Visible && field.Optional == "N" && Utils.isNullOrEmpty(field.Value)) || (!Utils.isNullOrEmpty(field.Value) && !pattern.test(field.Value))) {
            field.Valid = false;
            field.Touched = true;
        } else {
            field.Valid = true;
        }
    }

    static validateEmail = (field: FormSectionElements) => {
        const val = typeof field.Value === 'string' ? field.Value.trim() : field.Value;

        if (field.Optional !== 'Y' && Utils.isNullOrEmpty(val)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        }
        else if (Utils.isNotNullOrEmpty(val) && !validator.isEmail(val)) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = false;
        }
        else {
            field.Valid = true;
            field.IsEmpty = Utils.isNullOrEmpty(val);
        }
    }

    static validatePhoneNumber = (field: FormSectionElements) => {
        if ((field.Optional == "N" && Utils.isNullOrEmpty(field.Value))) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = true;
        } else {
            field.Valid = true;
        }

        if (!Utils.isNullOrEmpty(field.Value) && !validator.isMobilePhone(field.Value, "en-CA")) {
            field.Valid = false;
            field.Touched = true;
            field.IsEmpty = false;
        }

        if (!Utils.isNullOrEmpty(field.Value) && validator.isMobilePhone(field.Value, "en-CA")) {
            let val=field.Value;
            const digitsOnly = val.replace(/\D/g, ''); // \D = non-digit
            const match = digitsOnly.match(/^(\d{3})(\d{3})(\d{4})$/);
            field.Value = `(${match![1]}) ${match![2]}-${match![3]}`;
        }
    }

    static validateDropdown = (field: FormSectionElements) => {
        if (field.Optional == "N" && Utils.isNullOrEmpty(field.Value)) {
            field.IsEmpty = true;
            field.Valid = false;
            field.Touched = true;
        } else if (field.MultipleSelectionsProps?.allowMultipleSelection && field.Value?.length > field.MultipleSelectionsProps.maxNumberOfSelection) {
            field.IsEmpty = false;
            field.Valid = false;
            field.Touched = true;
        } else {
            field.Valid = true;
        }
    }

    static validateCheckbobWithInput = (field: FormSectionElements) => {
        const val = field.Value;
        const programContainer = getRootComponent(field);
        //console.log(programContainer)
        const isComplete = programContainer.IsComplete;
        //console.log(isComplete)
        if (isComplete) {
            field.Valid = true;
        } else {
            if ((field.Checked && !val) || (val && !field.Checked)) {
                field.Valid = false;
                field.Touched = true;
                field.IsEmpty = true;
            } else {
                field.Valid = true;
            }

            if (field.Valid) {
                const pattern = /^[0-9]{9}([A-Z]{2}[0-9]{4})?$/;
                if (field.Checked && (Utils.isNullOrEmpty(field.Value) || !pattern.test(field.Value))) {
                    field.Valid = false;
                    field.Touched = true;
                    field.IsEmpty = false;
                } else {
                    field.Valid = true;
                }
            }
        }

        if (field.Script) {
            const customJSFunc = eval(`(${field.Script})`);
            customJSFunc(field);
        }
    }

    static validateBXPCheckboxWithInput = (field: FormSectionElements) => {
        const val = field.Value;
        const programContainer = getRootComponent(field);
        //console.log(programContainer)
        const isComplete = programContainer.IsComplete;
        //console.log(isComplete)
        if (isComplete) {
            field.Valid = true;
        } else {
            if ((field.Checked && !val) || (val && !field.Checked)) {
                field.Valid = false;
                field.Touched = true;
                field.IsEmpty = true;
            } else {
                field.Valid = true;
            }
            if (field.Valid) {
                const pattern = /^[a-zA-Z0-9]{1,24}$/;
                if (field.Checked && !pattern.test(field.Value)) {
                    field.Valid = false;
                    field.Touched = true;
                    field.IsEmpty = false;
                } else {
                    field.Valid = true;
                }
            }
        }

        if (field.Script) {
            const customJSFunc = eval(`(${field.Script})`);
            customJSFunc(field);
        }
    }

    static validateFileUpload = (field: FormSectionElements) => {
        const value = field.Value;
        if ((!Array.isArray(value) || value?.length===0) && field.Optional === 'N') {
            field.Valid = false;
            field.IsEmpty = true;
            field.Touched = true;
            field.isContinueFile = true;
            field.ErrorMessage = field.EmptyErrorMessage || 'File must to be uploaded';
            field.ErrorMessageFRA = field.EmptyErrorMessageFRA || 'Le fichier doit être téléchargé';
        } else {
            // Check if any files have errors (for inline-error mode)
            const hasFileErrors = Array.isArray(value) && value.some((file: any) => file.hasError);

            if (hasFileErrors) {
                field.Valid = false;
                field.IsEmpty = false;
                // Don't set error messages for inline-error mode - errors are shown inline
                if (field.FileUpload?.UploadStyle !== 'inline-error') {
                    field.ErrorMessage = field.ErrorMessage || 'One or more files contain errors';
                    field.ErrorMessageFRA = field.ErrorMessageFRA || 'Un ou plusieurs fichiers contiennent des erreurs';
                }
            } else {
                field.Valid = true;
                field.IsEmpty = false;
                field.ErrorMessage = null;
                field.ErrorMessageFRA = null;
            }
        }
    }

    static getTechSection=(programContainer: ProgramContainer)=>{
        const forms= programContainer.ListOfFormSections.filter(p=>p.IsTechErrorPage);
        if(forms.length>0){
            return forms[0];
        }

        return null;
    }

    static getSectionIndex=(programContainer: ProgramContainer, sectionTitle: string)=>{
        const index= programContainer.ListOfFormSections.findIndex(p=>p.ENUTitle==sectionTitle);
        return index;
    }

    static resetSectionValidation=(section: FormSections)=>{
        section.ListOfFormSubSections.forEach(sub=>{
            sub.ListOfFormSectionElements.forEach(field=>{
                field.Valid=true;
                field.ErrorMessage=null;
                field.ErrorMessageFRA=null;
            })
        })
    }

    static healthCardOnInputValidation = (value: string, field: FormSectionElements) => {
        if (value.startsWith('0')) {
            return false;
        }

        const regex1 = "(\\d{4})";
        const regex2 = "(\\d{3})";
        const part1 = 4, part2 = 3, part3 = 3, part4 = 2;
        const val = value.replace(/\s+|-+/g, '');
        let regex = null;
        let fregex = null;

        if (val.length <= part1) {
            regex = new RegExp(`^\\d{${val.length}}$`);
            if (regex.test(val)) {
                field.Value = val.length === part1 ? val + '-' : val;
                return true;
            }
            return false;
        }

        if (val.length <= (part1 + part2)) {
            regex = new RegExp(`^${regex1}(\\d{${val.length - part1}})$`);
            if (regex.test(val)) {
                const value = val.replace(regex, '$1-$2');
                field.Value = val.length === (part1 + part2) ? value + '-' : value;
                return true;
            }
            return false;
        }

        if (val.length <= (part1 + part2 + part3)) {
            regex = new RegExp(`^${regex1}${regex2}(\\d{${val.length - part1 - part2}})$`);
            if (regex.test(val)) {
                const value = val.replace(regex, '$1-$2-$3');
                field.Value = val.length === (part1 + part2 + part3) ? value + '-' : value;
                return true;
            }
            return false;
        }

        if (val.length <= (part1 + part2 + part3 + part4)) {
            regex = new RegExp(`^${regex1}${regex2}${regex2}([a-z]{${val.length - part1 - part2 - part3}})$`, 'ig');
            if (regex.test(val)) {
                field.Value = val.replace(regex, '$1-$2-$3-$4').toUpperCase();
                return true;
            }
            return false;
        }
        return false;
    }

    static isDelOrBackspaceKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        return e.key === 'Delete' || e.key === 'Backspace';
    }
}
